/**
 * Initialize Shops and Quests
 *
 * Registers all shop and quest definitions with their respective managers.
 * Called during app initialization.
 */

import { outfitterManager } from '../shops/OutfitterManager';
import { marketManager } from '../shops/MarketManager';
import { questManager } from '../quests/QuestManager';
import { questBoardManager } from '../quests/QuestBoardManager';
import { customQuestManager } from '../quests/CustomQuestManager';
import { questChainManager } from '../quests/QuestChainManager';
import { equipmentManager } from '../equipment/EquipmentManager';
import { milestoneManager } from '../milestones/MilestoneManager';
import { useGameStore } from '@/state';

import { OUTFITTER_DEFINITIONS, MARKET_DEFINITIONS, TUTORIAL_QUEST_DEFINITIONS, LAKE_LEGEND_DEFINITIONS, DEFAULT_QUEST_CHAINS, TRAP_QUEST_DEFINITIONS, TRAP_QUEST_CHAINS } from '@/data';

/**
 * Initialize all shops and quests
 * Must be called before using shop/quest systems
 */
export function initializeShopsAndQuests(): void {
  console.log('[ShopQuest] Initializing shops and quests...');

  // Register outfitters
  outfitterManager.registerOutfitters(OUTFITTER_DEFINITIONS);
  console.log(`[ShopQuest] Registered ${OUTFITTER_DEFINITIONS.length} outfitters`);

  // Register markets
  marketManager.registerMarkets(MARKET_DEFINITIONS);
  console.log(`[ShopQuest] Registered ${MARKET_DEFINITIONS.length} markets`);

  // Register default quest definitions (tutorial + lake legend progression)
  marketManager.registerQuestDefinitions(TUTORIAL_QUEST_DEFINITIONS);
  marketManager.registerQuestDefinitions(LAKE_LEGEND_DEFINITIONS);
  console.log(`[ShopQuest] Registered ${TUTORIAL_QUEST_DEFINITIONS.length + LAKE_LEGEND_DEFINITIONS.length} default quest definitions`);

  // Register trap quest definitions
  marketManager.registerQuestDefinitions(TRAP_QUEST_DEFINITIONS);
  console.log(`[ShopQuest] Registered ${TRAP_QUEST_DEFINITIONS.length} trap quest definitions`);

  // Load custom quests and chains from localStorage
  customQuestManager.load();
  questChainManager.load();
  questChainManager.registerDefaultChains([...DEFAULT_QUEST_CHAINS, ...TRAP_QUEST_CHAINS]);
  marketManager.loadCustomQuests();

  // Restore accepted procedural quest definitions from save data
  // (procedural quests have unique IDs that don't survive page reload otherwise)
  const savedDefinitions = useGameStore.getState().playerState.quests.acceptedQuestDefinitions;
  if (savedDefinitions) {
    const defs = Object.values(savedDefinitions);
    if (defs.length > 0) {
      marketManager.registerQuestDefinitions(defs);
      console.log(`[ShopQuest] Restored ${defs.length} accepted procedural quest definitions`);
    }
  }

  // Restore quest board cache (so procedural quests + refill timers survive page reload)
  questBoardManager.loadCache();

  // Initialize managers
  outfitterManager.initialize();
  marketManager.initialize();
  questManager.initialize();

  // Recalculate equipment stats from saved equipped gear
  equipmentManager.recalculateStats();

  // Initialize milestone system
  milestoneManager.initialize();

  console.log('[ShopQuest] Initialization complete');
}

/**
 * Cleanup shops and quests
 * Called during app shutdown
 */
export function cleanupShopsAndQuests(): void {
  outfitterManager.cleanup();
  marketManager.cleanup();
  questManager.cleanup();
  milestoneManager.cleanup();
}
